import { MessageStatusType, useIM } from '@wui/im'
import { Plugin } from 'vue'

import router from '@/app/router'

import { getInitialAudioEnabledOnJoin, useCallSessionRoomStore } from '@/entities/call-session'
import { chatModel } from '@/entities/messages'
import { PreferenceKey, usePreferencesStore } from '@/entities/preference'
import { useReactionsStore } from '@/entities/reaction'
import { useStoreRooms, useStoreLastReadEventIds } from '@/entities/room'
import { STATUS_CODES, suspendPickupConnectedParty, useWebRTC } from '@/entities/webrtc'

import { isWebNotificationApiAvailable } from '@/shared/composables/browser-api'
import { useCallsNotifications } from '@/shared/composables/calls-notifications'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useAutoAnswer } from '@/shared/composables/use-auto-answer'
import { OSNotificationWrapped } from '@/shared/types/os-notification'
import { isElectron, tauriMode } from '@/shared/utils/import-meta'
import { getAnswerOptions, getOfferOptions } from '@/shared/utils/jssip'
import { optionalPush } from '@/shared/utils/router'
import { getChatRoute } from '@/shared/utils/url'

export const osNotificationPlugin: Plugin  = {
  install: () => {
    if (tauriMode()) {
      import('@tauri-apps/plugin-notification')
        .then(({ isPermissionGranted }) => isPermissionGranted())
        .then(async (isPermissionGranted) => {
          if (!isPermissionGranted) {
            const { requestPermission } = await import('@tauri-apps/plugin-notification')
            const permission = await requestPermission()
            return permission === 'granted'
          }
          return true
        })
        .then(async (isGranted) => {
          if (isGranted) {
            /**
             * TODO actions supports only on mobile platforms
            const { onAction } = await import('@tauri-apps/plugin-notification')
            onAction(() => {})
            */
          }
        })
    } else if (isElectron()) {
      // TODO
    } else if (!isWebNotificationApiAvailable()) {
      console.warn('Web Notifications API is not available in this browser context')
    } else {
      Notification.requestPermission((result: string) => {
        if (result === 'granted') {
          navigator.serviceWorker.addEventListener('message', (event: MessageEvent<OSNotificationWrapped>) => {
            const { type, action, data } = event.data
            if (type !== 'notificationclick') {
              return
            }
            switch (data.type) {
            case 'conferenceStarted': {
              if (action === 'join') {
                const { pServed } = data
                const { preferences } = usePreferencesStore()
                const room = useStoreRooms().findByPServed(pServed)
                const currentUserImLogin = useCurrentUser().currentUser.value?.imLogin
                const audio = room
                  ? getInitialAudioEnabledOnJoin(room, {
                    micDisabledOnStartConferenceGroup: !!preferences[PreferenceKey.MIC_DISABLED_ON_START_CONFERENCE_GROUP],
                    pushToTalkGroup: !!preferences[PreferenceKey.PUSH_TO_TALK_GROUP],
                    currentUserImLogin,
                  })
                  : false
                useAutoAnswer().registerAutoAnswer(pServed, audio, false, 60_000)
                const incomingSessionPromise = useWebRTC().waitForIncomingSession(60_000)
                return useIM().joinConference({ pServed })
                  .then(() => incomingSessionPromise.catch(() => undefined))
              }
              break
            }
            case 'pickup': {
              const { callId, targetNumber } = data
              if (action === 'pickupAnswer') {
                const restoreConnectedParty = suspendPickupConnectedParty(callId)
                useCallsNotifications().remove(callId)
                return getOfferOptions(true, false, false, `replaces:${callId}`).then((options) => {
                  restoreConnectedParty()
                  return useWebRTC().callAsync(targetNumber, options)
                })
              }
              break
            }
            case 'incomingCall': {
              const { sessionId } = data
              if (action) {
                const { getRTCSessionFacadeById } = useWebRTC()
                const facade = getRTCSessionFacadeById(sessionId)
                if (action === 'callAnswer') {
                  if (facade) {
                    const { preferences } = usePreferencesStore()
                    const room = useCallSessionRoomStore().getRoom(facade.sessionId)
                    const currentUserImLogin = useCurrentUser().currentUser.value?.imLogin
                    const audio = getInitialAudioEnabledOnJoin(room, {
                      micDisabledOnStartConferenceGroup: !!preferences[PreferenceKey.MIC_DISABLED_ON_START_CONFERENCE_GROUP],
                      pushToTalkGroup: !!preferences[PreferenceKey.PUSH_TO_TALK_GROUP],
                      currentUserImLogin,
                    })
                    return getAnswerOptions(audio, false, !!room?.isGroup).then((options) => {
                      return facade.answer(options)
                    })
                  }
                } else if (action === 'callReject') {
                  facade?.terminate({ status_code: STATUS_CODES.BUSY_HERE })
                }
              }
              break
            }
            case 'incomingMessage': {
              if (action?.startsWith('markAsRead')) {
                const { pServed, eventId, rank } = data
                const currentUserImLogin = useCurrentUser().currentUser.value?.imLogin
                if (currentUserImLogin) {
                  useIM().markAsRead({
                    to: pServed,
                    eventIdMarkedAsRead: eventId,
                  }).then((success) => {
                    if (success) {
                      chatModel.useChatMessageStore().updateMessageStatus(
                        eventId,
                        pServed,
                        currentUserImLogin,
                        MessageStatusType.READ,
                      )
                      useStoreLastReadEventIds().updateLastReadEventId(pServed, currentUserImLogin, eventId, rank)
                    }
                  })
                  if (action === 'markAsReadWithLike') {
                    useReactionsStore().makeOutgoingReaction({
                      reactionType: '❤️',
                      targetEventId: eventId,
                      to: pServed,
                    })
                  } else if (action === 'markAsReadWithThumbsUp') {
                    useReactionsStore().makeOutgoingReaction({
                      reactionType: '👍',
                      targetEventId: eventId,
                      to: pServed,
                    })
                  }
                }
              } else {
                optionalPush(getChatRoute(data.roomId, { hash: `${data.eventId}` }))
              }
              break
            }
            case 'callMissed': {
              const { targetNumber, roomId } = data
              if (action === 'makeCall' && targetNumber) {
                return getOfferOptions(true, false)
                  .then((options) => {
                    return useWebRTC().callAsync(targetNumber, options)
                  })
              } else if (!action && roomId) {
                window.focus()
                router.push({ name: 'Chat', params: { id: btoa(roomId) }})
              }
              break
            }
            case 'roomChanged':
            case 'pinMessage': {
              window.focus()
              const { roomId } = data
              optionalPush(getChatRoute(roomId))
              break
            }
            }
            return
          })
        }
      })
    }
  },
}
